import React, { useState, useEffect } from 'react'
import { Table, Button, message, Popconfirm, Input } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { PageHeader } from '../Common'
import {
  fetchFeatureFlags,
  addFeatureFlag,
  deleteFeatureFlag,
  useFeatureFlags,
} from '../../redux/slices/feature-flags'

const useStyles = createUseStyles({
  container: {
    width: '70%',
    margin: '0 auto',
    marginBottom: '1rem',
  },
  inputContainer: {
    display: 'flex',
    marginBottom: '1rem',
  },
  input: {
    marginRight: '1rem',
  },
})

export default function FeatureFlags() {
  const dispatch = useDispatch()
  const featureFlags = useFeatureFlags()
  const error = useSelector(state => state.featureFlags.error)
  const [newFeatureName, setNewFeatureName] = useState('')
  const [loading, setLoading] = useState('none')

  const classes = useStyles()

  useEffect(() => {
    setLoading('fetch')
    dispatch(fetchFeatureFlags())
    setLoading('none')
  }, [dispatch])

  const handleAddFeature = async () => {
    if (!newFeatureName.trim()) return
    setLoading('create')
    try {
      await addFeatureFlag(newFeatureName)
      await dispatch(fetchFeatureFlags())
      message.success('Feature flag added successfully!')
      setNewFeatureName('')
    } catch (e) {
      message.error('Failed to add feature flag')
    } finally {
      setLoading('none')
    }
  }

  const handleDelete = async id => {
    setLoading(`delete-${id}`)
    try {
      await deleteFeatureFlag(id)
      await dispatch(fetchFeatureFlags())
      message.success('Feature flag successfully removed')
    } catch (e) {
      message.error(`Error removing feature flag: ${e.message}`)
    } finally {
      setLoading('none')
    }
  }

  const columns = [
    {
      title: 'Feature Name',
      dataIndex: 'label',
      key: 'label',
      render: label => label,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this feature flag?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="link"
            icon="delete"
            className="delete-button"
            loading={loading === `delete-${record.id}`}
          />
        </Popconfirm>
      ),
    },
  ]

  if (error) {
    message.error(`Error fetching feature flags: ${error}`)
  }

  return (
    <div>
      <PageHeader title="Flag-enabled Features" />
      <div className={classes.container}>
        <div className={classes.inputContainer}>
          <Input
            placeholder="New feature flag name"
            value={newFeatureName}
            onChange={e => setNewFeatureName(e.target.value)}
            className={classes.input}
          />
          <Button
            type="primary"
            disabled={!newFeatureName.trim()}
            onClick={handleAddFeature}
            loading={loading === 'create'}
          >
            Add New Feature Flag
          </Button>
        </div>
        <Table
          size="middle"
          rowKey="id"
          loading={loading === 'fetch'}
          dataSource={featureFlags || []}
          columns={columns}
        />
      </div>
    </div>
  )
}
